/**
 * OrangeHRM is a comprehensive Human Resource Management (HRM) System that captures
 * all the essential functionalities required for any enterprise.
 * Copyright (C) 2006 OrangeHRM Inc., http://www.orangehrm.com
 *
 * OrangeHRM is free software: you can redistribute it and/or modify it under the terms of
 * the GNU General Public License as published by the Free Software Foundation, either
 * version 3 of the License, or (at your option) any later version.
 *
 * OrangeHRM is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
 * without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
 * See the GNU General Public License for more details.
 *
 * You should have received a copy of the GNU General Public License along with OrangeHRM.
 * If not, see <https://www.gnu.org/licenses/>.
 */

import {computed, ref, watch} from 'vue';
import {APIService} from '@/core/util/services/api.service';

export const ROSTER_STATUS_NOT_ELIGIBLE = 'NOT_ELIGIBLE';
export const ROSTER_STATUS_EN_ATTENTE = 'EN_ATTENTE';
export const ROSTER_STATUS_COMPLETED = 'COMPLETED';
export const ROSTER_STATUS_SIGNEE = 'SIGNEE';
export const ROSTER_STATUS_CLOS = 'CLOS';

export const ROSTER_SCOPE_MANAGER = 'manager';
export const ROSTER_SCOPE_ADMIN = 'admin';

export type RosterStatus =
  | typeof ROSTER_STATUS_NOT_ELIGIBLE
  | typeof ROSTER_STATUS_EN_ATTENTE
  | typeof ROSTER_STATUS_COMPLETED
  | typeof ROSTER_STATUS_SIGNEE
  | typeof ROSTER_STATUS_CLOS;

export type RosterScope =
  | typeof ROSTER_SCOPE_MANAGER
  | typeof ROSTER_SCOPE_ADMIN;

export type RosterEntry = {
  empNumber: number;
  firstName: string;
  lastName: string;
  fullName: string;
  status: RosterStatus;
  declarationId: number | null;
  declarationStatus: string | null;
};

export type Roster = {
  year: number;
  month: number;
  scope: RosterScope;
  includePastEmployees: boolean;
  total: number;
  entries: RosterEntry[];
};

export type UseRosterOptions = {
  scope: RosterScope;
  initialMonth?: string;
  includePastEmployeesDefault?: boolean;
  /**
   * Test seam: when provided, the composable uses this `Date` as
   * "now" for the default month (when `initialMonth` is unset).
   * Production code MUST leave this unset so the default month
   * tracks the real current month.
   */
  now?: Date;
};

/**
 * Page size for the roster tables. Matches the OrangeHRM
 * `FilterParams::DEFAULT_LIMIT` (50) so the backend default and
 * the frontend default agree when the client omits `limit`.
 */
export const ROSTER_PAGE_SIZE = 50;

function pad2(n: number): string {
  return n < 10 ? `0${n}` : String(n);
}

export function currentYearMonth(now: Date = new Date()): string {
  return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
}

export function parseYearMonth(
  value: string,
): {year: number; month: number} | null {
  const match = /^(\d{4})-(\d{2})$/.exec(value);
  if (!match) {
    return null;
  }
  const year = Number(match[1]);
  const month = Number(match[2]);
  if (month < 1 || month > 12) {
    return null;
  }
  return {year, month};
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

export function buildRosterUrl(scope: RosterScope): string {
  return scope === ROSTER_SCOPE_MANAGER
    ? '/api/v2/forfait-jours/rosters/manager'
    : '/api/v2/forfait-jours/rosters/admin';
}

export default function useRoster(options: UseRosterOptions) {
  const month = ref<string>(
    options.initialMonth ?? currentYearMonth(options.now ?? new Date()),
  );
  const includePastEmployees = ref<boolean>(
    options.includePastEmployeesDefault ?? false,
  );
  const roster = ref<Roster | null>(null);
  const isLoading = ref<boolean>(false);
  const error = ref<string | null>(null);

  // Pagination state. The backend serves a page slice in `data`
  // and the full count in `meta.total`. `currentPage` is 1-based;
  // `limit`/`offset` are derived from it. When the filter (month
  // or includePastEmployees) changes, the caller resets
  // `currentPage` to 1 before calling `load()` so the new filter
  // starts at page 1 (otherwise an offset past the new result set
  // would yield an empty page).
  const currentPage = ref<number>(1);
  const pageSize = ROSTER_PAGE_SIZE;
  const metaTotal = ref<number>(0);

  const http = new APIService(
    // @ts-expect-error: appGlobal is not in window object by default
    window.appGlobal.baseUrl,
    buildRosterUrl(options.scope),
  );

  async function load(): Promise<void> {
    isLoading.value = true;
    error.value = null;
    try {
      const params: Record<string, string> = {
        month: month.value,
        limit: String(pageSize),
        offset: String(pageSize * (currentPage.value - 1)),
      };
      if (options.scope === ROSTER_SCOPE_ADMIN) {
        params.includePastEmployees = String(includePastEmployees.value);
      }
      const response = await http.request({method: 'GET', params});
      if (response?.status >= 400) {
        error.value = `Server returned ${response.status}`;
        return;
      }
      const payload = response?.data?.data;
      if (!isPlainObject(payload)) {
        throw new Error('Malformed roster payload from server.');
      }
      roster.value = payload as unknown as Roster;
      const meta = response?.data?.meta;
      metaTotal.value =
        isPlainObject(meta) && typeof meta.total === 'number'
          ? meta.total
          : roster.value?.total ?? 0;
    } catch (e) {
      const message = e instanceof Error ? e.message : String(e);
      error.value = message;
    } finally {
      isLoading.value = false;
    }
  }

  const entries = computed<RosterEntry[]>(() => roster.value?.entries ?? []);
  // `total` is the FULL count across all pages (from `meta.total`),
  // not the size of the current page slice. The paginator uses it
  // to compute the number of pages.
  const total = computed<number>(() => metaTotal.value);
  const pages = computed<number>(() =>
    total.value > 0 ? Math.ceil(total.value / pageSize) : 1,
  );
  const showPaginator = computed<boolean>(() => total.value > pageSize);
  const isEmpty = computed<boolean>(() => total.value === 0);

  // Reload when the page changes (the filter changes are handled
  // by the page component, which resets `currentPage` to 1 first).
  watch(currentPage, () => {
    void load();
  });

  return {
    month,
    includePastEmployees,
    roster,
    entries,
    total,
    pages,
    pageSize,
    currentPage,
    showPaginator,
    isEmpty,
    isLoading,
    error,
    load,
  };
}

useRoster.parseYearMonth = parseYearMonth;
useRoster.currentYearMonth = currentYearMonth;
useRoster.buildRosterUrl = buildRosterUrl;
